import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { submitProfile, useMyProfile } from '@core/firebase/profile';
import { useSchooling } from '@core/firebase/curricula';
import { createGuardianInvite, useManagedBy } from '@core/firebase/children';
import type { Gender } from '@core/firebase/profile';
import { parsedDraftOf, validateProfileDraft, type ProfileProblem } from '@core/auth/profileDraft';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/cn';
import { Check, ChevronLeft, Copy, ShieldCheck, UserPlus } from 'lucide-react';
import {
  CardTitle, ChoicePills, Notice, PageHeader, SoftCard, field, pill,
} from '@/components/PageKit';
import { applyFormat, inputAttrs } from '@core/format/inputs';

/**
 * What someone may change about themselves after signing up.
 *
 * The same three fields and the same shared validator as the sign-up step —
 * the age floor is a product rule the Firestore rules also enforce, and a
 * second copy of it here would eventually disagree with both.
 *
 * It edits rather than re-asks: the form is filled from what is stored, so
 * changing a name does not mean retyping an age. That is why it waits for the
 * document instead of rendering empty boxes — a blank age offered as the
 * current one is an invitation to save over it with nothing.
 */
export default function ProfilePage() {
  const { t } = useLanguage();
  const { identity } = useAuth();
  const stored = useMyProfile(identity?.userId ?? null);
  const schooling = useSchooling(identity?.userId ?? null);

  const [name, setName] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState<Gender | null>(null);
  const [country, setCountry] = useState('');
  const [grade, setGrade] = useState('');
  const [problem, setProblem] = useState<ProfileProblem | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const { managedBy } = useManagedBy(identity?.userId ?? null);
  const [invite, setInvite] = useState<string | null>(null);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteFailed, setInviteFailed] = useState(false);
  const [copied, setCopied] = useState(false);

  /*
   * م-3-01: a guardian is linked by a code that comes from the PUPIL's side.
   * That is the whole design — nobody attaches themselves to a child by
   * knowing their phone number.
   */
  const makeInvite = async () => {
    if (inviteBusy) return;
    setInviteBusy(true);
    setInviteFailed(false);
    try {
      const out = await createGuardianInvite();
      setInvite(out.code);
      setCopied(false);
    } catch {
      setInviteFailed(true);
    } finally {
      setInviteBusy(false);
    }
  };

  // Fills the form once the stored profile lands. Keyed on the values rather
  // than on `loading` alone, so a later server-side change (an admin fixing a
  // name) is not silently discarded by a form that filled itself once.
  useEffect(() => {
    if (stored.loading) return;
    setName(stored.displayName);
    setAge(stored.age === null ? '' : String(stored.age));
    setGender(stored.gender);
  }, [stored.loading, stored.displayName, stored.age, stored.gender]);

  // Schooling arrives from its own listener, so it fills independently — a
  // reader who has never said a grade simply sees the pair empty.
  useEffect(() => {
    if (schooling.loading) return;
    setCountry(schooling.country ?? '');
    setGrade(schooling.grade === null ? '' : String(schooling.grade));
  }, [schooling.loading, schooling.country, schooling.grade]);

  const errorFor = (field: ProfileProblem['field']) =>
    problem && problem.field === field ? t(problem.key) : null;

  const save = async () => {
    if (busy) return;
    const values = { name, age, gender, country, grade };
    const found = validateProfileDraft(values);
    if (found) {
      setProblem(found);
      setSaved(false);
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      await submitProfile(parsedDraftOf(values));
      // Nothing navigates: this page is a destination, not a step. Saying so
      // is the only feedback there is, because the fields already show the
      // new values.
      setSaved(true);
    } catch {
      // A write failure belongs to no single field; it is shown under the
      // name so it is not lost, and save is the thing to press again.
      setProblem({ field: 'name', key: 'auth.nameStep.failed' });
      setSaved(false);
    } finally {
      setBusy(false);
    }
  };

  const copyInvite = async () => {
    if (!invite) return;
    try {
      await navigator.clipboard.writeText(invite);
      setCopied(true);
    } catch {
      // A browser that refuses the clipboard still shows the code, large,
      // to be read out or typed.
      setCopied(false);
    }
  };

  if (stored.loading) {
    return (
      <p data-testid="profile-loading" className="mx-auto w-full max-w-md p-6 text-center text-helper">
        {t('profile.loading')}
      </p>
    );
  }

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6">
      <PageHeader title={t('profile.title')} subtitle={t('profile.subtitle')} />

      {/*
        * Offered to a guest only, and FIRST, where a reader is already looking
        * at their own account. An account with a phone has nothing to claim.
        */}
      {stored.isGuest && (
        <Link
          to="/claim"
          data-testid="claim-offer"
          className="card-soft flex items-center gap-4 p-5 ring-2 ring-lime transition-colors hover:bg-surface"
        >
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-lime text-accent-foreground">
            <ShieldCheck className="h-6 w-6" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-semibold text-ink">{t('auth.claimNav')}</span>
            <span className="mt-1 block text-sm text-helper">{t('auth.claimExplains')}</span>
          </span>
          <ChevronLeft className="h-5 w-5 shrink-0 text-helper ltr:rotate-180" />
        </Link>
      )}

      <div className="grid items-start gap-6 lg:grid-cols-5">
        <SoftCard className="space-y-5 lg:col-span-3">

          <label className="block space-y-2">
            <span className="text-sm font-medium text-ink">{t('auth.nameStep.label')}</span>
            <input
              data-testid="name-input"
              value={name}
              onChange={(e) => { setName(applyFormat('personName', e.target.value)); setSaved(false); }}
              {...inputAttrs('personName')}
              placeholder={t('auth.nameStep.placeholder')}
              className={cn(field, errorFor('name') && 'border-danger')}
            />
            {errorFor('name') && (
              <p data-testid="name-error" role="alert" className="text-sm text-danger">{errorFor('name')}</p>
            )}
          </label>

          <div className="grid gap-4 sm:grid-cols-3">
            <label className="block space-y-2">
              <span className="text-sm font-medium text-ink">{t('auth.nameStep.ageLabel')}</span>
              <input
                data-testid="age-input"
                value={age}
                onChange={(e) => { setAge(applyFormat('age', e.target.value)); setSaved(false); }}
                placeholder={t('auth.nameStep.agePlaceholder')}
                {...inputAttrs('age')}
                className={cn(field, errorFor('age') && 'border-danger')}
              />
              {errorFor('age') && (
                <p data-testid="age-error" role="alert" className="text-sm text-danger">{errorFor('age')}</p>
              )}
            </label>

            {/*
              * The pair that selects a syllabus. Optional together: everyone who
              * signed up before curricula existed has neither, and requiring them
              * here would lock them out of editing their own name.
              */}
            <label className="block space-y-2">
              <span className="text-sm font-medium text-ink">{t('auth.nameStep.countryLabel')}</span>
              <select
                data-testid="country-input"
                value={country}
                onChange={(e) => { setCountry(e.target.value); setSaved(false); }}
                className={cn(field, errorFor('country') && 'border-danger')}
              >
                <option value="">—</option>
                <option value="SA">SA</option>
              </select>
              {errorFor('country') && (
                <p data-testid="country-error" role="alert" className="text-sm text-danger">{errorFor('country')}</p>
              )}
            </label>
            <label className="block space-y-2">
              <span className="text-sm font-medium text-ink">{t('auth.nameStep.gradeLabel')}</span>
              <select
                data-testid="grade-input"
                value={grade}
                onChange={(e) => { setGrade(e.target.value); setSaved(false); }}
                className={cn(field, errorFor('grade') && 'border-danger')}
              >
                <option value="">—</option>
                {Array.from({ length: 12 }, (_, i) => String(i + 1)).map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
              {errorFor('grade') && (
                <p data-testid="grade-error" role="alert" className="text-sm text-danger">{errorFor('grade')}</p>
              )}
            </label>
          </div>

          <fieldset className="space-y-2">
            <legend className="mb-2 text-sm font-medium text-ink">{t('auth.nameStep.genderLabel')}</legend>
            <ChoicePills
              value={gender}
              onChange={(g) => { setGender(g); setSaved(false); }}
              options={(['male', 'female'] as const).map((g) => ({
                value: g, label: t(`auth.nameStep.${g}`), testId: `gender-${g}`,
              }))}
            />
            {errorFor('gender') && (
              <p data-testid="gender-error" role="alert" className="text-sm text-danger">{errorFor('gender')}</p>
            )}
          </fieldset>

          <div className="flex flex-wrap items-center gap-3 pt-1">
            <button
              type="button"
              data-testid="save-profile"
              onClick={() => { void save(); }}
              disabled={busy}
              className={cn(pill.base, pill.md, pill.primary, 'min-w-[160px]')}
            >
              {t('profile.save')}
            </button>
            {saved && (
              <p data-testid="profile-saved" role="status" className="flex items-center gap-2 text-sm text-ink">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-lime text-accent-foreground">
                  <Check className="h-3.5 w-3.5" />
                </span>
                {t('profile.saved')}
              </p>
            )}
          </div>
        </SoftCard>

        {/* A managed child cannot issue one — their account is already owned —
            and the server refuses it, so the control is not offered either. */}
        {!managedBy && (
          <SoftCard testId="invite-section" className="space-y-4 lg:col-span-2">
            <CardTitle icon={<UserPlus />}>{t('children.inviteTitle')}</CardTitle>
            <p className="text-sm text-helper">{t('children.inviteWhy')}</p>

            {invite && (
              <div className="space-y-2 rounded-[18px] bg-tint px-4 py-5 text-center">
                <p className="text-sm text-helper">{t('children.inviteCodeLabel')}</p>
                <p
                  data-testid="invite-code"
                  dir="ltr"
                  className="text-[32px] font-semibold tracking-[0.3em] text-ink"
                >
                  {invite}
                </p>
                <p data-testid="invite-valid" className="text-xs text-helper">
                  {t('children.inviteValid')}
                </p>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                data-testid="make-invite"
                onClick={() => { void makeInvite(); }}
                disabled={inviteBusy}
                aria-busy={inviteBusy}
                className={cn(pill.base, pill.md, invite ? pill.quiet : pill.primary)}
              >
                {t('children.inviteAction')}
              </button>
              {invite && (
                <button
                  type="button"
                  data-testid="copy-invite"
                  onClick={() => { void copyInvite(); }}
                  className={cn(pill.base, pill.md, pill.lime)}
                >
                  <Copy className="h-4 w-4" />
                  {copied ? t('children.inviteCopied') : t('children.inviteCopy')}
                </button>
              )}
            </div>
            {inviteFailed && <Notice tone="danger" testId="invite-error">{t('children.inviteFailed')}</Notice>}
          </SoftCard>
        )}
      </div>
    </div>
  );
}
